import React, { useRef, useState } from 'react';
import { errorLines, saveProjectDetails } from '../../api';
import type { Project } from '../../types';
import { Button, InlineAlert, Modal, TextField, Toggle, useToast } from '../../ui';
import { buildReference, buildSettings } from './createProjectFlow';
import { SURVEY_URL_EXAMPLE, SURVEY_URL_HINT, surveyUrlError } from './surveyUrl';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const yes = (v: unknown, fallback = false) => (v === undefined || v === null ? fallback : String(v).toLowerCase() === 'yes');

interface Values {
  devices: { mobiles: boolean; desktops: boolean; tablets: boolean };
  requiresWebcam: boolean;
  restrictRegion: boolean;
  collectsPII: boolean;
  preventDuplicates: boolean;
  surveyUrl: string;
  contactName: string;
  phone: string;
  email: string;
}

const fromProject = (project: Project): Values => {
  const s = project.projectSettings || {};
  const ref = project.projectReference || {};
  return {
    devices: { mobiles: yes(s.mobiles), desktops: yes(s.desktops), tablets: yes(s.tablets) },
    requiresWebcam: yes(s.requiresWebcam),
    restrictRegion: yes(s.restrictRegion),
    collectsPII: yes(s.collectPersonalInfo) || yes(project.collectsPII),
    preventDuplicates: yes(s.preventDuplicates, true), // the backend's default is "yes"
    surveyUrl: project.surveyUrl || '',
    contactName: ref.contactName || '',
    phone: ref.phone || '',
    email: ref.email || '',
  };
};

interface CampaignDetailsDialogProps {
  project: Project;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Edit a campaign's survey / respondent settings and contact. Saved with PATCH /project/update/{id}: the settings
 * are merged into the existing ones on the server, and the campaign's categories and target groups are sent
 * unchanged because that endpoint always replaces them (see saveProjectDetails).
 * The name, dates and status are not edited here: the update endpoint does not take the name, and the status has
 * its own action on the page.
 */
export function CampaignDetailsDialog({ project, open, onClose, onSaved }: CampaignDetailsDialogProps) {
  const { notify } = useToast();
  const [values, setValues] = useState<Values>(() => fromProject(project));
  const [emailError, setEmailError] = useState('');
  const [urlError, setUrlError] = useState('');
  const [deviceError, setDeviceError] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const had = project.projectReference || {};

  // Start from the saved values every time the dialog opens, so a cancelled edit does not linger.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setValues(fromProject(project)); setEmailError(''); setUrlError(''); setDeviceError(''); setProblems([]); }
  }

  const set = <K extends keyof Values>(key: K, value: Values[K]) => setValues((v) => ({ ...v, [key]: value }));
  const setDevice = (key: keyof Values['devices']) => (checked: boolean) => setValues((v) => ({ ...v, devices: { ...v.devices, [key]: checked } }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current) return;
    const email = values.email.trim();
    const badEmail = email && !EMAIL_PATTERN.test(email) ? 'Enter a valid email address.' : '';
    const noDevice = !Object.values(values.devices).some(Boolean) ? 'Select at least one device type.' : '';
    const badUrl = surveyUrlError(values.surveyUrl);
    setEmailError(badEmail);
    setUrlError(badUrl);
    setDeviceError(noDevice);
    if (badEmail || noDevice || badUrl) return;

    // Send the contact when something is filled in, or to clear fields that were saved before.
    const filled = buildReference(values);
    const hadAny = Boolean(had.contactName || had.phone || had.email);
    const reference = filled || (hadAny ? { contactName: '', phone: '', email: '' } : undefined);

    savingRef.current = true;
    setSaving(true);
    setProblems([]);
    try {
      // an emptied URL is sent as null, which clears it
      const surveyUrl = values.surveyUrl.trim() || null;
      await saveProjectDetails(project, { projectSettings: buildSettings(values), surveyUrl, ...(reference ? { projectReference: reference } : {}) });
      notify('Campaign details saved', { tone: 'ok' });
      onSaved();
      onClose();
    } catch (err) {
      setProblems(errorLines(err));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      size="lg"
      title="Edit campaign details"
      subtitle={project.name}
      onClose={() => { if (!savingRef.current) onClose(); }}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form="campaign-details-form" loading={saving}>Save details</Button>
        </>
      )}
    >
      <form id="campaign-details-form" onSubmit={submit} noValidate className="space-y-6">
        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-ink">Survey is suitable for <span className="text-danger">*</span></legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <Toggle label="Mobile" checked={values.devices.mobiles} onChange={setDevice('mobiles')} />
            <Toggle label="Desktop" checked={values.devices.desktops} onChange={setDevice('desktops')} />
            <Toggle label="Tablet" checked={values.devices.tablets} onChange={setDevice('tablets')} />
          </div>
          {deviceError && <p className="mt-2 text-xs text-danger" role="alert">{deviceError}</p>}
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-ink">Respondent settings</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Toggle label="Requires webcam" description="Respondents need a camera" checked={values.requiresWebcam} onChange={(c) => set('requiresWebcam', c)} />
            <Toggle label="Restrict region" description="Limit to chosen regions" checked={values.restrictRegion} onChange={(c) => set('restrictRegion', c)} />
            <Toggle label="Collects PII" description="Personal information is collected" checked={values.collectsPII} onChange={(c) => set('collectsPII', c)} />
            <Toggle label="Prevent duplicates" description="Each respondent counts once" checked={values.preventDuplicates} onChange={(c) => set('preventDuplicates', c)} />
          </div>
        </fieldset>

        <TextField label="Survey URL" type="url" inputMode="url" placeholder={SURVEY_URL_EXAMPLE} value={values.surveyUrl} maxLength={2000} onChange={(e) => set('surveyUrl', e.target.value)} error={urlError} hint={`${SURVEY_URL_HINT} A target group can use its own (Basics step).`} />

        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-ink">Contact <span className="font-normal text-muted">(optional)</span></legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <TextField label="Contact name" value={values.contactName} maxLength={120} onChange={(e) => set('contactName', e.target.value)} />
            <TextField label="Phone" type="tel" value={values.phone} maxLength={40} onChange={(e) => set('phone', e.target.value)} />
            <TextField label="Email" type="email" value={values.email} maxLength={160} onChange={(e) => set('email', e.target.value)} error={emailError} />
          </div>
        </fieldset>

        {problems.length > 0 && (
          <InlineAlert>
            {problems.length === 1 ? problems[0] : <><b>{problems[0]}</b><ul className="ml-4 mt-1 list-disc">{problems.slice(1).map((m) => <li key={m}>{m}</li>)}</ul></>}
          </InlineAlert>
        )}
      </form>
    </Modal>
  );
}
